import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import ProviderBadge, { isStripeProvider } from './ProviderBadge';
import OrderTimeline from './OrderTimeline';
import { formatElapsed } from './timeAgo';
import { supabase } from '@shared/lib/supabase';
import { POLYGONSCAN_BASE_URL } from '@shared/constants/blockchain';
import type { Order, OrderStatus, CallbackAttempt } from '@shared/types';

function getBackPath(pathname: string): string {
  if (pathname.includes('/stripe/')) return '/dashboard/stripe/orders';
  if (pathname.includes('/crypto/')) return '/dashboard/crypto/orders';
  return '/dashboard/home';
}

function extractStripeMetadata(order: Order) {
  const meta = order.metadata || {};
  const verification = order.verification || {};
  return {
    customerName: (meta.customer_name || meta.customerName || verification.customer_name || null) as string | null,
    customerEmail: (meta.customer_email || meta.customerEmail || verification.customer_email || null) as string | null,
    paymentMethod: (meta.payment_method_type || meta.paymentMethodType || verification.payment_method_type || null) as string | null,
    cardBrand: (meta.card_brand || meta.cardBrand || verification.card_brand || null) as string | null,
    cardLast4: (meta.card_last4 || meta.cardLast4 || verification.card_last4 || null) as string | null,
    receiptUrl: (meta.receipt_url || meta.receiptUrl || verification.receipt_url || null) as string | null,
    stripePaymentIntentId: (meta.payment_intent_id || meta.stripe_payment_intent_id || verification.payment_intent_id || null) as string | null,
    chargeId: (meta.charge_id || verification.charge_id || null) as string | null,
  };
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 font-mono text-xs transition-colors"
      style={{ color: 'var(--dash-text-muted)' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
      title={`Copy ${label || 'value'}`}
    >
      <span className="text-[10px]">{copied ? '\u2713' : '\u2398'}</span>
      {copied ? 'Copied' : label || 'Copy'}
    </button>
  );
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [order, setOrder] = useState<Order | null>(null);
  const [callbacks, setCallbacks] = useState<CallbackAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const [expandedCallback, setExpandedCallback] = useState<string | null>(null);

  const backPath = getBackPath(location.pathname);

  useEffect(() => {
    async function load() {
      if (!id) return;
      const { data: orderData } = await supabase
        .from('orders')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (orderData) setOrder(orderData as unknown as Order);

      const { data: cbData } = await supabase
        .from('callback_attempts')
        .select('*')
        .eq('order_id', id)
        .order('attempt_number', { ascending: true });

      if (cbData) setCallbacks(cbData as unknown as CallbackAttempt[]);
      setLoading(false);
    }
    load();
  }, [id]);

  const copyVerifyLink = () => {
    if (!order?.txid) return;
    navigator.clipboard.writeText(`${window.location.origin}/verify/${order.txid}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const enrichedTimeline = useMemo(() => {
    if (!order) return [];
    const events = [...order.timeline];
    const eventTexts = events.map(e => e.event.toLowerCase());

    if (order.status === 'completed' && !eventTexts.some(t => t.includes('completed') || t.includes('settled'))) {
      events.push({ event: 'Payment completed', timestamp: order.completed_at || order.updated_at, details: {} });
    }
    if (order.status === 'expired' && !eventTexts.some(t => t.includes('expired') || t.includes('abandoned'))) {
      events.push({ event: 'Checkout abandoned', timestamp: order.expires_at, details: {} });
    }
    if (order.status === 'failed' && !eventTexts.some(t => t.includes('failed'))) {
      events.push({ event: 'Payment failed', timestamp: order.updated_at, details: {} });
    }
    if (order.status === 'on_hold' && !eventTexts.some(t => t.includes('hold'))) {
      events.push({ event: 'Order placed on hold', timestamp: order.updated_at, details: {} });
    }
    if (order.status === 'refunded' && !eventTexts.some(t => t.includes('refund'))) {
      events.push({ event: 'Payment refunded', timestamp: order.updated_at, details: {} });
    }
    if (order.status === 'authorized' && !eventTexts.some(t => t.includes('authorized'))) {
      events.push({ event: 'Payment authorized', timestamp: order.updated_at, details: {} });
    }
    return events;
  }, [order]);

  const elapsedSinceCreation = order
    ? formatElapsed(Date.now() - new Date(order.created_at).getTime())
    : '--';

  if (loading) {
    return (
      <div className="p-6">
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading order...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Order not found.</p>
        <Link
          to={backPath}
          className="font-sans text-sm mt-2 inline-block transition-colors"
          style={{ color: 'var(--dash-text-muted)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
        >
          Back to orders
        </Link>
      </div>
    );
  }

  const isStripe = isStripeProvider(order.provider, order.payment_provider);
  const stripeMeta = isStripe ? extractStripeMetadata(order) : null;
  const refundEvent = order.timeline.find(e => e.event === 'refund_processed');

  const hasAmountDiff = order.paid_amount != null && order.paid_amount > 0 && order.paid_amount !== order.expected_amount;

  return (
    <div className="p-6">
      <Link
        to={backPath}
        className="inline-flex items-center gap-1.5 font-sans text-sm transition-colors mb-5"
        style={{ color: 'var(--dash-text-dim)' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-dim)'; }}
      >
        <span className="text-[13px]">{'\u2190'}</span> Back to orders
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>
            Order {order.id.slice(0, 8)}...
          </h1>
          <CopyButton text={order.id} label="" />
          <StatusBadge status={order.status as OrderStatus} />
          <ProviderBadge provider={order.provider} paymentProvider={order.payment_provider} />
        </div>
        <div className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
          {elapsedSinceCreation} ago
        </div>
      </div>

      {hasAmountDiff && (
        <div className="border border-amber-500/20 rounded bg-amber-500/5 px-4 py-3 mb-5 flex items-center justify-between">
          <span className="font-sans text-xs text-amber-300">Amount mismatch detected</span>
          <span className="font-mono text-xs text-amber-400">
            Expected ${order.expected_amount.toFixed(2)} / Paid ${order.paid_amount!.toFixed(2)}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          {order.status === 'refunded' && (
            <div className="border border-purple-500/20 rounded bg-purple-500/5 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-mono text-[10px] text-purple-400 uppercase tracking-widest">Refund Details</h2>
                <StatusBadge status="refunded" />
              </div>
              <dl className="space-y-3 text-sm font-sans">
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Refund Amount</dt>
                  <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>
                    ${refundEvent?.details?.amount != null
                      ? Number(refundEvent.details.amount).toFixed(2)
                      : order.expected_amount.toFixed(2)}
                  </dd>
                </div>
                {refundEvent?.details?.reason && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Reason</dt>
                    <dd className="font-sans" style={{ color: 'var(--dash-text-tertiary)' }}>{String(refundEvent.details.reason)}</dd>
                  </div>
                )}
                {refundEvent?.details?.refund_id && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Refund ID</dt>
                    <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{String(refundEvent.details.refund_id)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Refund Date</dt>
                  <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                    {refundEvent
                      ? new Date(refundEvent.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                      : new Date(order.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {order.status === 'expired' && (
            <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-zinc-500" />
                <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Checkout Abandoned</h2>
              </div>
              <p className="font-sans text-sm leading-relaxed" style={{ color: 'var(--dash-text-muted)' }}>
                This checkout was abandoned. The customer reached the payment page but did not complete their purchase within the time window.
              </p>
            </div>
          )}

          {order.status === 'authorized' && (
            <div className="border border-blue-500/20 rounded bg-blue-500/5 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-mono text-[10px] text-blue-400 uppercase tracking-widest">Payment Authorized</h2>
                <StatusBadge status="authorized" />
              </div>
              <p className="font-sans text-sm leading-relaxed mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                This payment has been authorized but not yet captured. The merchant can capture it from WooCommerce by changing the order status to Processing or Completed.
              </p>
              <p className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>Authorization expires in ~7 days.</p>
            </div>
          )}

          <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>Order Details</h2>
            <dl className="space-y-3 text-sm font-sans">
              <div className="flex justify-between">
                <dt style={{ color: 'var(--dash-text-muted)' }}>Expected Amount</dt>
                <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>${order.expected_amount.toFixed(2)}</dd>
              </div>
              {order.paid_amount != null && order.paid_amount > 0 && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Paid Amount</dt>
                  <dd className={`font-mono ${hasAmountDiff ? 'text-amber-400' : ''}`} style={hasAmountDiff ? undefined : { color: 'var(--dash-text-secondary)' }}>
                    ${order.paid_amount.toFixed(2)}
                  </dd>
                </div>
              )}
              {order.verified_amount != null && order.verified_amount > 0 && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Verified Amount</dt>
                  <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>${order.verified_amount.toFixed(2)}</dd>
                </div>
              )}
              {order.original_amount != null && order.original_currency && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Original Amount</dt>
                  <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>
                    {order.original_amount.toFixed(2)} {order.original_currency.toUpperCase()}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt style={{ color: 'var(--dash-text-muted)' }}>Provider</dt>
                <dd><ProviderBadge provider={order.provider} paymentProvider={order.payment_provider} /></dd>
              </div>
              {order.external_order_id && (
                <div className="flex justify-between items-center">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>External ID</dt>
                  <dd className="flex items-center gap-2">
                    <span className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{order.external_order_id}</span>
                    <CopyButton text={order.external_order_id} />
                  </dd>
                </div>
              )}
              {order.customer_country && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Customer Country</dt>
                  <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{order.customer_country}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt style={{ color: 'var(--dash-text-muted)' }}>Tolerance</dt>
                <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>{(order.tolerance * 100).toFixed(0)}%</dd>
              </div>
              {isStripe ? (
                <>
                  <div className="pt-3" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
                    <dt style={{ color: 'var(--dash-text-muted)' }} className="mb-1">Settlement</dt>
                    <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                      {order.status === 'completed'
                        ? 'Settled via Stripe Connect'
                        : 'Settlement pending'}
                    </dd>
                  </div>
                  {order.provider_session_id && (
                    <div className="flex justify-between items-center">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Stripe Payment ID</dt>
                      <dd className="flex items-center gap-2">
                        <span className="font-mono text-xs truncate max-w-[200px]" style={{ color: 'var(--dash-text-muted)' }}>
                          {order.provider_session_id}
                        </span>
                        <CopyButton text={order.provider_session_id} />
                      </dd>
                    </div>
                  )}
                  {order.provider_session_id?.startsWith('pi_') && (
                    <div className="flex justify-between items-center">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Stripe Dashboard</dt>
                      <dd className="relative group">
                        <a
                          href={`https://dashboard.stripe.com/payments/${order.provider_session_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs inline-flex items-center gap-1 transition-colors"
                          style={{ color: 'var(--dash-text-tertiary)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                        >
                          View in Stripe <span className="text-[10px]">{'\u2197'}</span>
                        </a>
                        <span
                          className="absolute bottom-full left-0 mb-1.5 hidden group-hover:block rounded px-2 py-1 font-sans text-[10px] whitespace-nowrap z-10"
                          style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)', color: 'var(--dash-text-muted)' }}
                        >
                          Only accessible to platform admin
                        </span>
                      </dd>
                    </div>
                  )}
                  {order.fee_amount != null && order.fee_amount > 0 && (
                    <div className="flex justify-between">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Platform Fee</dt>
                      <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>${order.fee_amount.toFixed(2)}</dd>
                    </div>
                  )}
                  {stripeMeta?.stripePaymentIntentId && stripeMeta.stripePaymentIntentId !== order.provider_session_id && (
                    <div className="flex justify-between items-center">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Payment Intent</dt>
                      <dd className="flex items-center gap-2">
                        <span className="font-mono text-xs truncate max-w-[200px]" style={{ color: 'var(--dash-text-muted)' }}>
                          {stripeMeta.stripePaymentIntentId}
                        </span>
                        <CopyButton text={stripeMeta.stripePaymentIntentId} />
                      </dd>
                    </div>
                  )}
                  {stripeMeta?.chargeId && (
                    <div className="flex justify-between items-center">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Charge ID</dt>
                      <dd className="flex items-center gap-2">
                        <span className="font-mono text-xs truncate max-w-[200px]" style={{ color: 'var(--dash-text-muted)' }}>{stripeMeta.chargeId}</span>
                        <CopyButton text={stripeMeta.chargeId} />
                      </dd>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="pt-3" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
                    <dt style={{ color: 'var(--dash-text-muted)' }} className="mb-1">Settlement</dt>
                    <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                      {order.status === 'completed'
                        ? 'Settled on-chain (USDC on Polygon)'
                        : 'Awaiting payment'}
                    </dd>
                  </div>
                  {order.deposit_address && (
                    <div className="flex justify-between items-center">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Deposit Address</dt>
                      <dd className="flex items-center gap-2">
                        <span className="font-mono text-xs truncate max-w-[200px]" style={{ color: 'var(--dash-text-muted)' }}>{order.deposit_address}</span>
                        <CopyButton text={order.deposit_address} />
                      </dd>
                    </div>
                  )}
                  {order.merchant_wallet && (
                    <div className="flex justify-between items-center">
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Merchant Wallet</dt>
                      <dd className="flex items-center gap-2">
                        <span className="font-mono text-xs truncate max-w-[200px]" style={{ color: 'var(--dash-text-muted)' }}>{order.merchant_wallet}</span>
                        <CopyButton text={order.merchant_wallet} />
                      </dd>
                    </div>
                  )}
                  {order.txid && (
                    <div className="flex justify-between items-center pt-3" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
                      <dt style={{ color: 'var(--dash-text-muted)' }}>Transaction</dt>
                      <dd className="flex items-center gap-2">
                        <a
                          href={`${POLYGONSCAN_BASE_URL}/tx/${order.txid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs inline-flex items-center gap-1 transition-colors"
                          style={{ color: 'var(--dash-text-tertiary)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                        >
                          {order.txid.slice(0, 16)}...
                          <span className="text-[10px]">{'\u2197'}</span>
                        </a>
                        <CopyButton text={order.txid} />
                      </dd>
                    </div>
                  )}
                  {order.txid && (
                    <div>
                      <dt style={{ color: 'var(--dash-text-muted)' }} className="mb-1">Verification Link</dt>
                      <dd>
                        <button
                          onClick={copyVerifyLink}
                          className="inline-flex items-center gap-1.5 font-mono text-xs transition-colors"
                          style={{ color: 'var(--dash-text-tertiary)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                        >
                          <span className="text-[10px]">{copied ? '\u2713' : '\u29C9'}</span>
                          {copied ? 'Copied' : 'Copy shareable link'}
                        </button>
                      </dd>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between pt-3" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
                <dt style={{ color: 'var(--dash-text-muted)' }}>Created</dt>
                <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                  {new Date(order.created_at).toLocaleString()}
                </dd>
              </div>
              {order.completed_at && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--dash-text-muted)' }}>Completed</dt>
                  <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                    {new Date(order.completed_at).toLocaleString()}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt style={{ color: 'var(--dash-text-muted)' }}>Expires</dt>
                <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                  {new Date(order.expires_at).toLocaleString()}
                </dd>
              </div>
            </dl>
          </div>

          {isStripe && stripeMeta && (stripeMeta.customerName || stripeMeta.customerEmail || stripeMeta.cardBrand) && (
            <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>Customer & Payment</h2>
              <dl className="space-y-3 text-sm font-sans">
                {stripeMeta.customerName && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Customer Name</dt>
                    <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>{stripeMeta.customerName}</dd>
                  </div>
                )}
                {stripeMeta.customerEmail && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Customer Email</dt>
                    <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{stripeMeta.customerEmail}</dd>
                  </div>
                )}
                {stripeMeta.cardBrand && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Card</dt>
                    <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>
                      {stripeMeta.cardBrand.charAt(0).toUpperCase() + stripeMeta.cardBrand.slice(1)}
                      {stripeMeta.cardLast4 && ` \u00B7\u00B7\u00B7\u00B7${stripeMeta.cardLast4}`}
                    </dd>
                  </div>
                )}
                {!stripeMeta.cardBrand && stripeMeta.paymentMethod && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Payment Method</dt>
                    <dd className="font-mono" style={{ color: 'var(--dash-text-secondary)' }}>{stripeMeta.paymentMethod.replace(/_/g, ' ')}</dd>
                  </div>
                )}
                {stripeMeta.receiptUrl && (
                  <div className="flex justify-between">
                    <dt style={{ color: 'var(--dash-text-muted)' }}>Receipt</dt>
                    <dd>
                      <a
                        href={stripeMeta.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs inline-flex items-center gap-1 transition-colors"
                        style={{ color: 'var(--dash-text-tertiary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                      >
                        View receipt <span className="text-[10px]">{'\u2197'}</span>
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>Timeline</h2>
            <OrderTimeline events={enrichedTimeline} />
          </div>

          {callbacks.length > 0 && (
            <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>
                Callback Attempts ({callbacks.length})
              </h2>
              <div className="space-y-2">
                {callbacks.map((cb) => (
                  <div key={cb.id}>
                    <button
                      onClick={() => setExpandedCallback(expandedCallback === cb.id ? null : cb.id)}
                      className="w-full flex items-center justify-between text-xs font-sans px-2 py-1 rounded transition-colors"
                      style={{ borderBottom: '1px solid var(--dash-border-light)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ color: 'var(--dash-text-muted)' }}>Attempt {cb.attempt_number}</span>
                      <span className={cb.success ? 'text-emerald-400' : 'text-red-400'}>
                        {cb.success ? 'Success' : `Failed (${cb.response_code || 'N/A'})`}
                      </span>
                      <span className="font-mono" style={{ color: 'var(--dash-text-dim)' }}>
                        {cb.response_time_ms ? `${cb.response_time_ms}ms` : '--'}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                        {expandedCallback === cb.id ? '\u25BE' : '\u25B8'}
                      </span>
                    </button>
                    {expandedCallback === cb.id && (
                      <div className="mt-1 mb-2 px-2 py-2 rounded" style={{ background: 'var(--dash-input)', border: '1px solid var(--dash-border-light)' }}>
                        <div className="font-mono text-[10px] mb-1" style={{ color: 'var(--dash-text-dim)' }}>Response Code: {cb.response_code || '--'}</div>
                        <div className="font-mono text-[10px] mb-1" style={{ color: 'var(--dash-text-dim)' }}>Time: {cb.response_time_ms ? `${cb.response_time_ms}ms` : '--'}</div>
                        <div className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>
                          Sent: {new Date(cb.created_at).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            <button
              onClick={() => setShowRawData(!showRawData)}
              className="w-full flex items-center justify-between"
            >
              <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
                Raw Data
              </h2>
              <span className="font-mono text-xs" style={{ color: 'var(--dash-text-faint)' }}>
                {showRawData ? '\u25BE Hide' : '\u25B8 Show'}
              </span>
            </button>
            {showRawData && (
              <pre
                className="mt-3 p-3 rounded overflow-x-auto font-mono text-[11px] max-h-80 overflow-y-auto"
                style={{ background: 'var(--dash-input)', border: '1px solid var(--dash-border-light)', color: 'var(--dash-text-muted)' }}
              >
                {JSON.stringify(order, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
